import { CILogger } from 'common/log/log.provider';

import type { OktaSessionCheckResult } from './fetchOktaSession';
import { readSharedCheck, writeSharedCheck } from './sharedOktaSessionCheckStore';
import type { SharedOktaSessionCheck } from './sharedOktaSessionCheckStore';

/** How long an answer from Okta is shared with every tab before a tab asks Okta again. */
export const OKTA_SESSION_CHECK_SHARED_WINDOW_MS = 10000;

export const OKTA_SESSION_CHECK_LOCK_NAME = 'oktaSessionCheck';

/** A shared answer stamped further ahead than this came from a skewed or edited clock and is ignored. */
const FUTURE_STAMP_TOLERANCE_MS = 10000;

const HTTP_TOO_MANY_REQUESTS = 429;

export interface CoordinatedOktaSessionCheck {
    result: OktaSessionCheckResult;
    /**
     * Whether this call asked Okta itself. Only that call acts on the result; every other tab learns about a logout
     * through the shared `activeUser` (see `useBrowserTabSync`).
     */
    ranCheck: boolean;
}

let checkInFlight: Promise<CoordinatedOktaSessionCheck> | null = null;

let missingWebLocksReported = false;

/**
 * Checks the Okta session with at most one request to Okta across all open tabs and windows.
 *
 * A call made while this tab already has a check running joins it. Across tabs, a Web Lock lets one tab ask Okta
 * while the others wait, and the answer is shared through IndexedDB: a tab that gets the lock after another tab has
 * answered reuses that answer instead of asking Okta again. An answer from Okta is reused for
 * {@link OKTA_SESSION_CHECK_SHARED_WINDOW_MS}, and so is a rate limit; any other check Okta gave no usable answer to
 * is only shared with the tabs that were already waiting for it, so the next trigger checks again.
 */
export function runCoordinatedOktaSessionCheck(
    fetchSession: () => Promise<OktaSessionCheckResult>
): Promise<CoordinatedOktaSessionCheck> {
    if (checkInFlight) {
        return checkInFlight.then(({ result }) => ({ result, ranCheck: false }));
    }

    const check = checkAcrossTabs(fetchSession).finally(() => {
        checkInFlight = null;
    });
    checkInFlight = check;

    return check;
}

function checkAcrossTabs(fetchSession: () => Promise<OktaSessionCheckResult>): Promise<CoordinatedOktaSessionCheck> {
    const requestedAt = Date.now();
    const checkUnlessAnswered = () => reuseSharedCheckOrFetch(requestedAt, fetchSession);

    if (!supportsWebLocks()) {
        reportMissingWebLocks();

        return checkUnlessAnswered();
    }

    let lockGranted = false;
    const checkWithLock = () => {
        lockGranted = true;

        return checkUnlessAnswered();
    };

    return (
        navigator.locks.request(OKTA_SESSION_CHECK_LOCK_NAME, checkWithLock) as Promise<CoordinatedOktaSessionCheck>
    ).catch((error: unknown) => {
        // The check itself failed while holding the lock: running it again without the lock would only repeat it.
        if (lockGranted) {
            throw error;
        }

        CILogger.getInstance().warn('Okta session check: the Web Lock failed, checking without it', error);

        return checkUnlessAnswered();
    });
}

async function reuseSharedCheckOrFetch(
    requestedAt: number,
    fetchSession: () => Promise<OktaSessionCheckResult>
): Promise<CoordinatedOktaSessionCheck> {
    const shared = await reusableSharedCheck(requestedAt);

    if (shared) {
        return {
            result: { outcome: shared.outcome, expiresAt: shared.expiresAt, httpStatus: shared.httpStatus },
            ranCheck: false,
        };
    }

    const result = await fetchSession();
    // Awaited so the lock is only released once the answer is saved for the tab that gets it next.
    await writeSharedCheck({
        completedAt: Date.now(),
        outcome: result.outcome,
        expiresAt: result.expiresAt,
        httpStatus: result.httpStatus,
    });

    return { result, ranCheck: true };
}

async function reusableSharedCheck(requestedAt: number): Promise<SharedOktaSessionCheck | null> {
    const shared = await readSharedCheck();
    const now = Date.now();

    if (!shared || shared.completedAt > now + FUTURE_STAMP_TOLERANCE_MS) {
        return null;
    }

    // Finished while this call waited for the lock, so it answers the same burst of triggers.
    if (shared.completedAt >= requestedAt) {
        return shared;
    }

    if (isReusedForSharedWindow(shared) && now - shared.completedAt < OKTA_SESSION_CHECK_SHARED_WINDOW_MS) {
        return shared;
    }

    return null;
}

/**
 * Answers from Okta are reused for the shared window. Other ambiguous answers are not, so the next trigger checks
 * again, except a rate limit: Okta asked to slow down, so asking again on every tab switch would only add to it.
 */
function isReusedForSharedWindow({ outcome, httpStatus }: SharedOktaSessionCheck): boolean {
    return outcome !== 'ambiguous' || httpStatus === HTTP_TOO_MANY_REQUESTS;
}

function supportsWebLocks(): boolean {
    return typeof navigator !== 'undefined' && !!navigator.locks;
}

function reportMissingWebLocks(): void {
    if (missingWebLocksReported) {
        return;
    }

    missingWebLocksReported = true;
    CILogger.getInstance().warn(
        'Okta session check: this browser has no Web Locks, so tabs checking at the same moment may each ask Okta',
        null
    );
}
